import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
  AvatarGroup,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Save,
  Cancel,
  Build as BuildIcon,
  Schedule,
  CheckCircle,
  LocationOn,
  AccessTime,
  Person,
  Warning,
  Info,
  Assignment,
  Group,
  Notes,
  CloudQueue,
  Star,
  MoreVert,
  Delete,
  PlayArrow,
  Pause,
  Stop,
} from '@mui/icons-material';

interface Installation {
  id: string;
  projectId: string;
  projectName: string;
  customer: string;
  customerId: string;
  status: 'new_install' | 'scheduled' | 'in_progress' | 'completed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  scheduledDate: string;
  startTime: string;
  endTime: string;
  estimatedDuration: number;
  actualDuration: number;
  location: string;
  installer: string;
  team: string[];
  equipment: string[];
  notes: string;
  weatherDependent: boolean;
  specialRequirements: string[];
  createdAt: string;
  updatedAt: string;
}

const InstallationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [installation, setInstallation] = useState<Installation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Installation>>({});
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');

  useEffect(() => {
    fetchInstallation();
  }, [id]);

  const fetchInstallation = async () => {
    try {
      setLoading(true);
      // Mock data - replace with actual API call
      const mockInstallation: Installation = {
        id: id || '1',
        projectId: '1',
        projectName: 'Luxury Home LED Installation',
        customer: 'John & Sarah Johnson',
        customerId: '1',
        status: 'in_progress',
        priority: 'high',
        scheduledDate: '2024-10-20',
        startTime: '08:00',
        endTime: '17:00',
        estimatedDuration: 8,
        actualDuration: 4,
        location: '123 Oak Street, Beverly Hills, CA',
        installer: 'Mike Rodriguez',
        team: ['Mike Rodriguez', 'Alex Chen', 'Sarah Wilson'],
        equipment: ['LED Strips', 'Dimmer Switches', 'Control Panel', 'Wire'],
        notes: 'Customer prefers warm white lighting in living areas. High ceilings require lift equipment.',
        weatherDependent: false,
        specialRequirements: ['Lift equipment needed for high ceilings', 'Customer prefers morning installation'],
        createdAt: '2024-10-15T10:00:00Z',
        updatedAt: '2024-10-20T12:30:00Z'
      };
      
      setInstallation(mockInstallation);
      setEditData(mockInstallation);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch installation details');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      // Mock save - replace with actual API call
      console.log('Saving installation:', editData);
      setInstallation(prev => prev ? { ...prev, ...editData } : null);
      setEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save installation');
    }
  };

  const handleCancel = () => {
    setEditData(installation || {});
    setEditing(false);
  };

  const handleStatusChange = async () => {
    try {
      // Mock status change - replace with actual API call
      console.log('Changing status to:', newStatus);
      setInstallation(prev => prev ? { ...prev, status: newStatus as any } : null);
      setStatusDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to change status');
    }
  };

  const handleDelete = async () => {
    try {
      // Mock delete - replace with actual API call
      console.log('Deleting installation:', id);
      navigate('/installs');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete installation');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'scheduled': return 'info';
      case 'new_install': return 'primary';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle />;
      case 'in_progress': return <BuildIcon />;
      case 'scheduled': return <Schedule />;
      case 'new_install': return <Assignment />;
      default: return <BuildIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#D4AF37' }} />
      </Box>
    );
  }

  if (error || !installation) {
    return (
      <Box sx={{ backgroundColor: '#1A1A1A', minHeight: '100vh', p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Installation not found'}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/installs')}
          sx={{ backgroundColor: '#D4AF37', color: '#000000' }}
        >
          Back to Installs
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#1A1A1A', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton
            onClick={() => navigate('/installs')}
            sx={{ color: '#D4AF37' }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ color: '#D4AF37', fontWeight: 700 }}>
            {installation.projectName}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          {!editing ? (
            <>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={handleEdit}
                sx={{ color: '#D4AF37', borderColor: '#D4AF37' }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                startIcon={getStatusIcon(installation.status)}
                onClick={() => {
                  setNewStatus(installation.status);
                  setStatusDialogOpen(true);
                }}
                sx={{ color: '#D4AF37', borderColor: '#D4AF37' }}
              >
                Change Status
              </Button>
              <IconButton
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ color: '#F44336' }}
              >
                <Delete />
              </IconButton>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                sx={{ backgroundColor: '#D4AF37', color: '#000000' }}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCancel}
                sx={{ color: '#F44336', borderColor: '#F44336' }}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Status and Priority Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                {getStatusIcon(installation.status)}
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>Status</Typography>
                  <Chip
                    label={installation.status.replace('_', ' ').toUpperCase()}
                    color={getStatusColor(installation.status) as any}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Star sx={{ color: '#D4AF37' }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>Priority</Typography>
                  <Chip
                    label={installation.priority.toUpperCase()}
                    color={getPriorityColor(installation.priority) as any}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <AccessTime sx={{ color: '#D4AF37' }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>Duration</Typography>
                  <Typography variant="h6" sx={{ color: '#D4AF37' }}>
                    {installation.actualDuration > 0 
                      ? `${installation.actualDuration}h` 
                      : `${installation.estimatedDuration}h`
                    }
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTab-root': { color: '#AAAAAA' },
            '& .Mui-selected': { color: '#D4AF37' },
            '& .MuiTabs-indicator': { backgroundColor: '#D4AF37' },
          }}
        >
          <Tab label="Overview" />
          <Tab label="Team & Equipment" />
          <Tab label="Timeline" />
          <Tab label="Notes & Requirements" />
        </Tabs>

        <CardContent>
          {/* Overview Tab */}
          {tabValue === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Project Details
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Customer
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                    {editing ? (
                      <TextField
                        fullWidth
                        value={editData.customer || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, customer: e.target.value }))}
                        sx={{ '& .MuiInputBase-input': { color: '#FFFFFF' } }}
                      />
                    ) : (
                      installation.customer
                    )}
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Location
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn fontSize="small" />
                    {editing ? (
                      <TextField
                        fullWidth
                        value={editData.location || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, location: e.target.value }))}
                        sx={{ '& .MuiInputBase-input': { color: '#FFFFFF' } }}
                      />
                    ) : (
                      installation.location
                    )}
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Scheduled Date & Time
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Schedule fontSize="small" />
                    {formatDate(installation.scheduledDate)} at {formatTime(installation.startTime)} - {formatTime(installation.endTime)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Installation Progress
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Estimated Duration
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                    {installation.estimatedDuration} hours
                  </Typography>
                </Box>
                {installation.actualDuration > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                      Actual Duration
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                      {installation.actualDuration} hours
                    </Typography>
                  </Box>
                )}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Weather Dependent
                  </Typography>
                  <Chip
                    label={installation.weatherDependent ? 'Yes' : 'No'}
                    color={installation.weatherDependent ? 'warning' : 'success'}
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Team & Equipment Tab */}
          {tabValue === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Team Members
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Person sx={{ color: '#D4AF37' }} />
                  <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                    Lead Installer: {installation.installer}
                  </Typography>
                </Box>
                <AvatarGroup max={4} sx={{ mb: 2 }}>
                  {installation.team.map((member, index) => (
                    <Avatar key={index} sx={{ backgroundColor: '#D4AF37', color: '#000000' }}>
                      {member.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                  ))}
                </AvatarGroup>
                <List>
                  {installation.team.map((member, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Person sx={{ color: '#D4AF37' }} />
                      </ListItemIcon>
                      <ListItemText primary={member} sx={{ color: '#FFFFFF' }} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Equipment & Materials
                </Typography>
                <List>
                  {installation.equipment.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <BuildIcon sx={{ color: '#D4AF37' }} />
                      </ListItemIcon>
                      <ListItemText primary={item} sx={{ color: '#FFFFFF' }} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          )}

          {/* Timeline Tab */}
          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                Installation Timeline
              </Typography>
              <Paper sx={{ backgroundColor: '#1A1A1A', p: 2, border: '1px solid #333333' }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Assignment sx={{ color: '#2196F3' }} />
                  <Box>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                      Project Created
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                      {formatDate(installation.createdAt)}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Schedule sx={{ color: '#4CAF50' }} />
                  <Box>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                      Scheduled
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                      {formatDate(installation.scheduledDate)} at {formatTime(installation.startTime)}
                    </Typography>
                  </Box>
                </Box>
                {installation.status === 'in_progress' && (
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <BuildIcon sx={{ color: '#FF9800' }} />
                    <Box>
                      <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                        In Progress
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                        Started at {formatTime(installation.startTime)}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {installation.status === 'completed' && (
                  <Box display="flex" alignItems="center" gap={2}>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                    <Box>
                      <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                        Completed
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                        Finished in {installation.actualDuration} hours
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Box>
          )}

          {/* Notes & Requirements Tab */}
          {tabValue === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Notes
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={editData.notes || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                    sx={{
                      '& .MuiInputBase-input': { color: '#FFFFFF' },
                      '& .MuiOutlinedInput-root': { borderColor: '#555555' },
                    }}
                  />
                ) : (
                  <Paper sx={{ backgroundColor: '#1A1A1A', p: 2, border: '1px solid #333333' }}>
                    <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                      {installation.notes}
                    </Typography>
                  </Paper>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Special Requirements
                </Typography>
                <List>
                  {installation.specialRequirements.map((requirement, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Warning sx={{ color: '#FF9800' }} />
                      </ListItemIcon>
                      <ListItemText primary={requirement} sx={{ color: '#FFFFFF' }} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Status Change Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1A1A1A',
            border: '1px solid #333333',
            color: '#FFFFFF',
          }
        }}
      >
        <DialogTitle sx={{ color: '#D4AF37' }}>Change Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel sx={{ color: '#AAAAAA' }}>New Status</InputLabel>
            <Select
              value={newStatus}
              label="New Status"
              onChange={(e) => setNewStatus(e.target.value)}
              sx={{ color: '#FFFFFF' }}
            >
              <MenuItem value="new_install">New Install</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setStatusDialogOpen(false)} 
            sx={{ color: '#FFFFFF' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleStatusChange} 
            autoFocus 
            sx={{ color: '#D4AF37' }}
          >
            Change Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1A1A1A',
            border: '1px solid #333333',
            color: '#FFFFFF',
          }
        }}
      >
        <DialogTitle sx={{ color: '#D4AF37' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#FFFFFF' }}>
            Are you sure you want to delete this installation? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            sx={{ color: '#FFFFFF' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            autoFocus 
            sx={{ color: '#F44336' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstallationDetail;
